import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { formatDateTime } from "../lib/datetime.js";
import { DefaultAgentModel } from "./DefaultAgentModel.js";

interface BusinessRow {
  id: string;
  name: string;
}

interface AgentConfigRow {
  business_id: string;
  agent_name: string;
  agent_model: string;
  agent_model_configured: string;
  agent_fallback_model: string | null;
  agent_consecutive_failures: number;
  agent_failure_threshold: number;
  agent_last_error: unknown;
  agent_last_error_at: string | null;
  agent_last_promotion_at: string | null;
  voice: string;
  prompt_overlay: string | null;
  summary_enabled: boolean;
  responses_reasoning_effort: string | null;
}

/** Reasoning effort for the backend model (Responses delegation). Empty = the model's own default. */
const REASONING_EFFORTS = ["none", "minimal", "low", "medium", "high", "xhigh"] as const;

interface VoiceRow {
  voice_name: string;
  gender: string;
  accent_label: string;
}

/**
 * The admin surface for docs/architecture.md section 5A. Shows the configured-vs-active
 * split directly (a badge whenever the model actually running differs from what admin
 * asked for -- meaning it auto-promoted), and gives admin the one recovery action:
 * re-arm the configured model via rpc_agent_reconfigure_retry, exactly as the backend
 * expects (services/voice/src/agent/backendAgent.ts never self-heals on its own).
 */
export function AgentConfig() {
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [businessId, setBusinessId] = useState<string>("");
  const [voices, setVoices] = useState<VoiceRow[]>([]);
  const [cfg, setCfg] = useState<AgentConfigRow | null>(null);
  const [draft, setDraft] = useState<Partial<AgentConfigRow>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from("businesses")
      .select("id, name")
      .order("name")
      .then(({ data }) => {
        const list = (data ?? []) as BusinessRow[];
        setBusinesses(list);
        if (list[0]) setBusinessId(list[0].id);
      });
    supabase
      .from("available_voices")
      .select("voice_name, gender, accent_label")
      .eq("active", true)
      .then(({ data }) => setVoices((data ?? []) as VoiceRow[]));
  }, []);

  useEffect(() => {
    if (!businessId) return;
    setMessage(null);
    supabase
      .from("agent_configs")
      .select("*")
      .eq("business_id", businessId)
      .single()
      .then(({ data }) => {
        const row = data as AgentConfigRow | null;
        setCfg(row);
        setDraft(row ?? {});
      });
  }, [businessId]);

  async function handleSave() {
    if (!cfg) return;
    setSaving(true);
    setMessage(null);

    const configuredChanged = draft.agent_model_configured !== undefined && draft.agent_model_configured !== cfg.agent_model_configured;

    const update: Record<string, unknown> = {
      agent_name: draft.agent_name,
      agent_model_configured: draft.agent_model_configured,
      voice: draft.voice,
      prompt_overlay: draft.prompt_overlay,
      summary_enabled: draft.summary_enabled,
      responses_reasoning_effort: draft.responses_reasoning_effort || null,
    };
    // Mirrors the rule in docs section 5A: changing the configured model gives it an
    // immediate chance to run (agent_model := agent_model_configured), WITHOUT touching
    // agent_fallback_model, which stays pointed at whatever last actually worked.
    if (configuredChanged) {
      update.agent_model = draft.agent_model_configured;
      update.agent_consecutive_failures = 0;
    }

    const { error } = await supabase.from("agent_configs").update(update).eq("business_id", businessId);
    setSaving(false);
    if (error) {
      setMessage(`Save failed: ${error.message}`);
      return;
    }
    setMessage("Saved.");
    const { data } = await supabase.from("agent_configs").select("*").eq("business_id", businessId).single();
    setCfg(data as AgentConfigRow);
    setDraft(data as AgentConfigRow);
  }

  async function handleRetryConfigured() {
    setSaving(true);
    setMessage(null);
    const { error } = await supabase.rpc("rpc_agent_reconfigure_retry", { p_business_id: businessId });
    setSaving(false);
    if (error) {
      setMessage(`Retry failed: ${error.message}`);
      return;
    }
    setMessage("Re-armed the configured model.");
    const { data } = await supabase.from("agent_configs").select("*").eq("business_id", businessId).single();
    setCfg(data as AgentConfigRow);
    setDraft(data as AgentConfigRow);
  }

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Agent config</h1>

      <DefaultAgentModel />

      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Business
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>

      {cfg && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: 14 }}>
          {cfg.agent_model !== cfg.agent_model_configured && (
            <div style={{ background: "var(--rc-warning-soft)", border: "1px solid var(--rc-warning)", borderRadius: 6, padding: 12 }}>
              <strong>Auto-demoted.</strong> Configured <code>{cfg.agent_model_configured}</code> kept failing, so calls are
              currently running on <code>{cfg.agent_model}</code> instead
              {cfg.agent_last_promotion_at ? ` (since ${formatDateTime(cfg.agent_last_promotion_at)})` : ""}.
            </div>
          )}

          <Field label="Agent name">
            <input
              value={draft.agent_name ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, agent_name: e.target.value }))}
              style={inputStyle}
            />
          </Field>

          <Field label="Configured model">
            <input
              value={draft.agent_model_configured ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, agent_model_configured: e.target.value }))}
              style={inputStyle}
            />
          </Field>

          <Field label="Reasoning effort (backend model)">
            <select
              value={draft.responses_reasoning_effort ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, responses_reasoning_effort: e.target.value || null }))}
              style={inputStyle}
            >
              <option value="">Model default</option>
              {REASONING_EFFORTS.map((effort) => (
                <option key={effort} value={effort}>
                  {effort}
                </option>
              ))}
            </select>
            <div style={{ color: "var(--rc-text-faint)", marginTop: 4 }}>
              Lower is faster, higher thinks harder. Applies to new calls only. Leave on "Model default" unless calls feel slow.
            </div>
          </Field>

          <Field label="Currently active model (read-only)">
            <input value={cfg.agent_model} disabled style={{ ...inputStyle, background: "var(--rc-surface-sunken)", color: "var(--rc-text-muted)" }} />
          </Field>

          <Field label="Fallback (last known-good, auto-maintained)">
            <input value={cfg.agent_fallback_model ?? "-"} disabled style={{ ...inputStyle, background: "var(--rc-surface-sunken)", color: "var(--rc-text-muted)" }} />
          </Field>

          <Field label={`Consecutive failures (threshold: ${cfg.agent_failure_threshold})`}>
            <span>{cfg.agent_consecutive_failures}</span>
            {cfg.agent_last_error_at && (
              <span style={{ color: "var(--rc-text-faint)", marginLeft: 8 }}>Last error: {formatDateTime(cfg.agent_last_error_at)}</span>
            )}
          </Field>

          <Field label="Voice">
            <select
              value={draft.voice ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, voice: e.target.value }))}
              style={inputStyle}
            >
              {voices.map((v) => (
                <option key={v.voice_name} value={v.voice_name}>
                  {v.voice_name} — {v.gender}, {v.accent_label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Prompt overlay (business-specific notes appended to the base prompt)">
            <textarea
              value={draft.prompt_overlay ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, prompt_overlay: e.target.value }))}
              rows={4}
              style={{ ...inputStyle, fontFamily: "inherit" }}
            />
          </Field>

          <Field label="Post-call summaries">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={draft.summary_enabled ?? false}
                onChange={(e) => setDraft((d) => ({ ...d, summary_enabled: e.target.checked }))}
              />
              Enabled (bills per call on success)
            </label>
          </Field>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => void handleSave()} disabled={saving} style={buttonStyle}>
              Save
            </button>
            <button
              onClick={() => void handleRetryConfigured()}
              disabled={saving || cfg.agent_model === cfg.agent_model_configured}
              style={buttonStyle}
              title="Re-arms agent_model to agent_model_configured and clears the failure counter"
            >
              Retry configured model now
            </button>
          </div>
          {message && <p style={{ color: message.includes("failed") ? "var(--rc-danger)" : "var(--rc-success)" }}>{message}</p>}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ color: "var(--rc-text-muted)", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = { display: "block", width: "100%", padding: 8, boxSizing: "border-box" };
const buttonStyle: React.CSSProperties = { padding: "8px 16px" };
